// dialogs/ResourceDialog/styled.js - Honey-themed modal styles
import { styled } from '@mui/material/styles';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Box, 
  Typography, 
  TextField, 
  FormControl, 
  Button,
  IconButton
} from '@mui/material';

// Honey-inspired color palette
export const honeyModalColors = {
  goldenHoney: '#F1C40F',
  brightHoney: '#F4D03F',
  honeycomb: '#D4A574',
  darkHoney: '#B8860B',
  honeyBrown: '#5D4E37',
  softCream: '#FFFEF7',
  warmAmber: '#CD853F',
  lightBrown: '#8B7355',
  honeyGradient: 'linear-gradient(135deg, #F1C40F, #F4D03F)',
  honeycombGradient: 'linear-gradient(135deg, #D4A574, #F1C40F)',
};

// Main modal container
export const HoneyDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: '24px',
    border: `3px solid ${honeyModalColors.goldenHoney}`,
    background: 'white',
    boxShadow: `0 20px 60px ${honeyModalColors.goldenHoney}30, 0 0 0 1px ${honeyModalColors.brightHoney}40`,
    overflow: 'hidden',
    minWidth: '500px',
    maxWidth: '600px',
    
    [theme.breakpoints.down('sm')]: {
      minWidth: '90vw',
      maxWidth: '95vw',
      margin: theme.spacing(2),
    },
  },
  
  '& .MuiBackdrop-root': {
    backgroundColor: `${honeyModalColors.honeyBrown}40`,
    backdropFilter: 'blur(8px)',
  },
}));

// Modal header
export const HoneyDialogTitle = styled(DialogTitle)(({ theme }) => ({
  background: honeyModalColors.honeycombGradient,
  color: honeyModalColors.honeyBrown,
  fontWeight: 700,
  fontSize: '1.5rem',
  padding: theme.spacing(3, 3, 2),
  textAlign: 'center',
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1),
  
  // Decorative honey accent
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: '50%',
    transform: 'translateX(-50%)',
    width: '60px',
    height: '4px',
    background: honeyModalColors.goldenHoney,
    borderRadius: '2px',
  },
}));

// Close button
export const HoneyCloseButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  right: theme.spacing(1),
  top: theme.spacing(1),
  color: honeyModalColors.honeyBrown,
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  borderRadius: '50%',
  width: 40,
  height: 40,
  transition: 'all 0.3s ease',
  
  '&:hover': {
    backgroundColor: honeyModalColors.brightHoney,
    transform: 'scale(1.1)',
    boxShadow: `0 4px 12px ${honeyModalColors.goldenHoney}40`,
  },
  
  '&:disabled': {
    opacity: 0.6,
    transform: 'none',
  },
}));

// Modal content
export const HoneyDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: theme.spacing(3),
  background: `linear-gradient(135deg, ${honeyModalColors.softCream} 0%, white 100%)`,
  
  // Subtle honey pattern
  backgroundImage: `
    radial-gradient(circle at 20% 20%, ${honeyModalColors.brightHoney}08 2px, transparent 2px),
    radial-gradient(circle at 80% 80%, ${honeyModalColors.goldenHoney}05 2px, transparent 2px)
  `,
  backgroundSize: '40px 40px, 60px 60px',
}));

// Form container within modal
export const HoneyModalForm = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
}));

// Text fields in modal
export const HoneyModalTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    background: 'white',
    transition: 'all 0.3s ease',
    
    '& fieldset': {
      borderColor: honeyModalColors.brightHoney,
      borderWidth: '2px',
    },
    
    '&:hover fieldset': {
      borderColor: honeyModalColors.goldenHoney,
    },
    
    '&.Mui-focused fieldset': {
      borderColor: honeyModalColors.goldenHoney,
      boxShadow: `0 0 0 3px ${honeyModalColors.goldenHoney}20`,
    },
    
    '&.Mui-error fieldset': {
      borderColor: honeyModalColors.warmAmber,
    },
  },
  
  '& .MuiInputBase-input': {
    fontSize: '1rem',
    color: honeyModalColors.honeyBrown,
    padding: theme.spacing(1.5),
    
    '&::placeholder': {
      color: honeyModalColors.lightBrown,
      opacity: 0.8,
    },
  },
  
  '& .MuiInputLabel-root': {
    color: honeyModalColors.honeyBrown,
    fontWeight: 600,
    fontSize: '0.95rem',
    
    '&.Mui-focused': {
      color: honeyModalColors.goldenHoney,
    },
    
    '&.Mui-error': {
      color: honeyModalColors.warmAmber,
    },
  },
  
  '& .MuiFormHelperText-root': {
    fontSize: '0.85rem',
    color: honeyModalColors.lightBrown,
    fontStyle: 'italic',
    
    '&.Mui-error': {
      color: honeyModalColors.warmAmber,
    },
  },
}));

// Form control for selects in modal
export const HoneyModalFormControl = styled(FormControl)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    background: 'white',
    transition: 'all 0.3s ease',
    
    '& fieldset': {
      borderColor: honeyModalColors.brightHoney,
      borderWidth: '2px',
    },
    
    '&:hover fieldset': {
      borderColor: honeyModalColors.goldenHoney,
    },
    
    '&.Mui-focused fieldset': {
      borderColor: honeyModalColors.goldenHoney,
      boxShadow: `0 0 0 3px ${honeyModalColors.goldenHoney}20`,
    },
    
    '&.Mui-error fieldset': {
      borderColor: honeyModalColors.warmAmber,
    },
    
    '&.Mui-disabled': {
      '& fieldset': {
        borderColor: honeyModalColors.lightBrown,
        opacity: 0.5,
      },
    },
  },
  
  '& .MuiSelect-select': {
    fontSize: '1rem',
    color: honeyModalColors.honeyBrown,
    padding: theme.spacing(1.5),
    
    '&.Mui-disabled': {
      color: honeyModalColors.lightBrown,
      opacity: 0.7,
    },
  },
  
  '& .MuiInputLabel-root': {
    color: honeyModalColors.honeyBrown,
    fontWeight: 600,
    fontSize: '0.95rem',
    
    '&.Mui-focused': {
      color: honeyModalColors.goldenHoney,
    },
    
    '&.Mui-error': {
      color: honeyModalColors.warmAmber,
    },
    
    '&.Mui-disabled': {
      color: honeyModalColors.lightBrown,
      opacity: 0.7,
    },
  },
  
  '& .MuiFormHelperText-root': {
    fontSize: '0.85rem',
    color: honeyModalColors.lightBrown,
    fontStyle: 'italic',
    
    '&.Mui-error': {
      color: honeyModalColors.warmAmber,
    },
  },
  
  '& .MuiSelect-icon': {
    color: honeyModalColors.honeycomb,
    
    '&.Mui-disabled': {
      color: honeyModalColors.lightBrown,
      opacity: 0.5,
    },
  },
}));

// Modal actions
export const HoneyDialogActions = styled(DialogActions)(({ theme }) => ({
  padding: theme.spacing(2, 3, 3),
  background: 'white',
  gap: theme.spacing(1),
  justifyContent: 'flex-end',
  
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    '& > *': {
      width: '100%',
    },
  },
}));

// Create button
export const HoneyCreateButton = styled(Button)(({ theme }) => ({
  background: honeyModalColors.honeyGradient,
  color: honeyModalColors.honeyBrown,
  borderRadius: '50px',
  padding: theme.spacing(1.2, 3),
  fontWeight: 700,
  fontSize: '1rem',
  boxShadow: `0 8px 25px ${honeyModalColors.goldenHoney}40`,
  transition: 'all 0.3s ease',
  textTransform: 'none',
  minWidth: '120px',
  
  '&:hover': {
    background: honeyModalColors.honeycombGradient,
    transform: 'translateY(-2px)',
    boxShadow: `0 12px 35px ${honeyModalColors.goldenHoney}60`,
  },
  
  '&:disabled': {
    opacity: 0.6,
    transform: 'none',
    background: honeyModalColors.honeyGradient,
  },
}));

// Cancel button
export const HoneyCancelButton = styled(Button)(({ theme }) => ({
  background: `${honeyModalColors.brightHoney}10`,
  color: honeyModalColors.honeyBrown,
  border: `2px solid ${honeyModalColors.brightHoney}`,
  borderRadius: '50px',
  padding: theme.spacing(1.2, 3),
  fontWeight: 600,
  fontSize: '1rem',
  transition: 'all 0.3s ease',
  textTransform: 'none',
  minWidth: '120px',
  
  '&:hover': {
    background: `${honeyModalColors.brightHoney}20`,
    borderColor: honeyModalColors.goldenHoney,
    transform: 'translateY(-1px)',
  },
  
  '&:disabled': {
    opacity: 0.6,
    transform: 'none',
  },
}));

// Section subtitle in modal
export const HoneyModalSubtitle = styled(Typography)(({ theme }) => ({
  color: honeyModalColors.lightBrown,
  fontSize: '0.95rem',
  marginBottom: theme.spacing(2),
  textAlign: 'center',
  fontStyle: 'italic',
}));

// Required field indicator
export const HoneyModalRequired = styled('span')(({ theme }) => ({
  color: honeyModalColors.warmAmber,
  fontWeight: 700,
}));

// Form section divider
export const HoneyModalDivider = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '2px',
  background: `linear-gradient(90deg, transparent, ${honeyModalColors.brightHoney}, transparent)`,
  margin: theme.spacing(2, 0),
}));